// src/pages/MarketplacePage.tsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import {
    ShoppingCart, Check, Search, X,
    Globe, Layers, BookOpen, ArrowRight,
    Star, LogIn, LogOut, User, ChevronDown,
} from 'lucide-react';
import './MarketplacePage.css';
import { Product } from '../types/marketplace.types';

const API_BASE_URL = '/api';

type MarketplaceTab = 'access_all' | 'combos' | 'decks';

const MarketplacePage: React.FC = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<MarketplaceTab>('access_all');
    const [searchQuery, setSearchQuery] = useState('');
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const { hasItem, addItem } = useCart();

    // ── Auth ──
    const token = localStorage.getItem('accessToken');
    const isLoggedIn = !!token;
    const userName = (() => {
        try {
            const raw = localStorage.getItem('user');
            if (raw) return JSON.parse(raw)?.name ?? null;
        } catch { }
        return null;
    })();

    useEffect(() => {
        if (!userMenuOpen) return;
        const handler = () => setUserMenuOpen(false);
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, [userMenuOpen]);

    const loadProducts = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/marketplace/products`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (response.ok) {
                const data = await response.json();
                setProducts(data.products);
            }
        } catch (error) {
            console.error('Erro ao carregar produtos:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    const tabProducts = useMemo(() => {
        const active = products.filter(p => p.active);
        return {
            access_all: active.filter(p => p.type === 'access_all'),
            combos: active.filter(p => p.type === 'combo' || p.type === 'cycle'),
            decks: active.filter(p => p.type === 'deck' || p.type === 'custom_deck')
        };
    }, [products]);

    const filteredDecks = useMemo(() => {
        const base = tabProducts.decks;
        if (!searchQuery.trim()) return base;
        const q = searchQuery.toLowerCase();
        return base.filter(p =>
            p.title.toLowerCase().includes(q) ||
            p.description?.toLowerCase().includes(q)
        );
    }, [tabProducts.decks, searchQuery]);

    const formatPrice = (cents: number) =>
        (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    const getMinPrice = (product: Product): number => {
        if (!product.plans?.length) return 0;
        return Math.min(...product.plans.map(p => p.priceCents));
    };

    const getPlanLabel = (months: number | null): string => {
        if (months === null) return 'Vitalício';
        if (months < 1) return 'Teste';
        if (months === 1) return '1 mês';
        if (months < 12) return `${months} meses`;
        const anos = months / 12;
        return `${anos} ano${anos > 1 ? 's' : ''}`;
    };

    const getProductSubtitle = (product: Product): string => {
        if (product.type === 'combo') return `${product.refDetails?.deckCount || '?'} decks inclusos`;
        if (product.type === 'cycle') return 'Campo de estudo completo';
        if (product.type === 'deck') return `${product.refDetails?.totalCards || 0} flashcards`;
        if (product.type === 'custom_deck') return 'Deck personalizado';
        return '';
    };

    const handleLogout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        setUserMenuOpen(false);
        window.location.reload();
    };

    if (loading) {
        return (
            <div className="mktpl-page">
                <div className="mktpl-loading">
                    <div className="mktpl-spinner" />
                    <p>Carregando produtos...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="mktpl-page">

            {/* ══ HEADER VERDE ══ */}
            <header className="mktpl-header">
                <div className="mktpl-header-inner">

                    {/* Logo + título */}
                    <div className="mktpl-header-brand" onClick={() => navigate('/')}>
                        <img src="/graia-logo.png" alt="GRAIA" className="mktpl-logo-img" />
                        <div className="mktpl-header-title-block">
                            <span className="mktpl-header-title">Acesso à Plataforma GRAIA</span>
                            <span className="mktpl-header-subtitle">Escolha o modelo que melhor se adapta à sua estrutura de estudo.</span>
                        </div>
                    </div>

                    {/* Direita: carrinho + auth */}
                    <div className="mktpl-header-actions">
                        <button className="mktpl-btn-cart" onClick={() => navigate('/checkout')}>
                            <ShoppingCart size={17} strokeWidth={2} />
                            Carrinho
                        </button>

                        {isLoggedIn ? (
                            <div className="mktpl-user-wrap" onClick={e => { e.stopPropagation(); setUserMenuOpen(v => !v); }}>
                                <button className="mktpl-btn-user">
                                    <div className="mktpl-user-avatar">
                                        {userName ? userName.charAt(0).toUpperCase() : <User size={15} />}
                                    </div>
                                    <span className="mktpl-user-name">{userName ?? 'Minha conta'}</span>
                                    <ChevronDown size={14} className={`mktpl-user-chevron ${userMenuOpen ? 'open' : ''}`} />
                                </button>

                                {userMenuOpen && (
                                    <div className="mktpl-user-menu">
                                        <button className="mktpl-user-menu-item" onClick={() => navigate('/dashboard')}>
                                            <BookOpen size={15} /> Ir para plataforma
                                        </button>
                                        <div className="mktpl-user-menu-divider" />
                                        <button className="mktpl-user-menu-item mktpl-logout" onClick={handleLogout}>
                                            <LogOut size={15} /> Sair da conta
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button className="mktpl-btn-login" onClick={() => navigate('/login')}>
                                <LogIn size={16} strokeWidth={2} />
                                Entrar
                            </button>
                        )}
                    </div>
                </div>
            </header>

            {/* ══ SELETOR DE CAMINHO (substitui tabs) ══ */}
            <div className="mktpl-path-selector">
                <div className="mktpl-path-inner">
                    {([
                        { id: 'access_all' as const, label: 'Acesso Total', icon: <Globe size={20} />, desc: 'Tudo na plataforma' },
                        { id: 'combos' as const, label: 'Combos & Ciclos', icon: <Layers size={20} />, desc: 'Coleção estruturada' },
                        { id: 'decks' as const, label: 'Decks Individuais', icon: <BookOpen size={20} />, desc: 'Acesso específico' },
                    ]).map((path, idx) => (
                        <button
                            key={path.id}
                            className={`mktpl-path-btn ${activeTab === path.id ? 'mktpl-path-btn--active' : ''}`}
                            onClick={() => { setActiveTab(path.id); setSearchQuery(''); }}
                        >
                            <span className="mktpl-path-btn-icon">{path.icon}</span>
                            <span className="mktpl-path-btn-label">{path.label}</span>
                            <span className="mktpl-path-btn-desc">{path.desc}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* ══ CONTEÚDO ══ */}
            <div className="mktpl-body">

                {/* ── ACESSO TOTAL ── */}
                {activeTab === 'access_all' && (
                    <div className="mktpl-section">
                        {tabProducts.access_all.length === 0 ? (
                            <div className="mktpl-empty">
                                <Globe size={52} strokeWidth={1.4} />
                                <p>Nenhum plano de Acesso Total disponível no momento.</p>
                            </div>
                        ) : (
                            <div className="mktpl-fullwidth-list">
                                {tabProducts.access_all.map(product => (
                                    <div
                                        key={product._id}
                                        className={`mktpl-hero-card ${product.alreadyPurchased ? 'mktpl-hero-card--owned' : ''}`}
                                    >
                                        <div className="mktpl-hero-card-grid" aria-hidden />

                                        {!product.alreadyPurchased && !hasItem(product._id) && (
                                            <span className="mktpl-hero-badge">
                                                <Star size={10} fill="currentColor" />
                                                Acesso completo à plataforma
                                            </span>
                                        )}
                                        {product.alreadyPurchased && (
                                            <span className="mktpl-hero-badge mktpl-hero-badge--owned">
                                                <Check size={11} strokeWidth={3} /> Você já possui
                                            </span>
                                        )}
                                        {hasItem(product._id) && !product.alreadyPurchased && (
                                            <span className="mktpl-hero-badge mktpl-hero-badge--cart">
                                                <Check size={11} strokeWidth={3} /> No carrinho
                                            </span>
                                        )}

                                        <div className="mktpl-hero-body">
                                            <div className="mktpl-hero-left">
                                                <h2 className="mktpl-hero-title">{product.title}</h2>
                                                {product.description && (
                                                    <p className="mktpl-hero-desc">{product.description}</p>
                                                )}
                                                <ul className="mktpl-hero-features">
                                                    {[
                                                        'Todos os decks e atualizações futuras',
                                                        'Todos os ciclos de estudo inclusos',
                                                        'Novos conteúdos adicionados automaticamente',
                                                        'Banco de questões completo',
                                                        'Métricas avançadas de desempenho',
                                                    ].map(item => (
                                                        <li key={item}>
                                                            <Check size={13} strokeWidth={2.5} />
                                                            {item}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>

                                            <div className="mktpl-hero-right">
                                                <div className="mktpl-hero-plans">
                                                    {product.plans.map((plan, idx) => (
                                                        <button
                                                            key={idx}
                                                            className="mktpl-hero-plan-btn"
                                                            onClick={() => {
                                                                if (product.alreadyPurchased) {
                                                                    navigate(`/marketplace/${product._id}`);
                                                                    return;
                                                                }
                                                                addItem(product, plan);
                                                                navigate('/checkout');
                                                            }}
                                                        >
                                                            <div className="mktpl-hero-plan-btn-left">
                                                                <span className="mktpl-hero-plan-label">{getPlanLabel(plan.durationMonths)}</span>
                                                                <span className="mktpl-hero-plan-price">{formatPrice(plan.priceCents)}</span>
                                                            </div>
                                                            <ArrowRight size={15} strokeWidth={2} />
                                                        </button>
                                                    ))}
                                                </div>

                                                <div className="mktpl-hero-trust">
                                                    <span><Check size={11} strokeWidth={3} /> Pagamento seguro via Mercado Pago</span>
                                                    <span><Check size={11} strokeWidth={3} /> Acesso imediato após confirmação</span>
                                                </div>

                                                {product.alreadyPurchased && (
                                                    <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', textAlign: 'center', margin: 0 }}>
                                                        Você já possui este produto
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* ── COMBOS & CICLOS ── */}
                {activeTab === 'combos' && (
                    <div className="mktpl-section">
                        {tabProducts.combos.length === 0 ? (
                            <div className="mktpl-empty">
                                <Layers size={52} strokeWidth={1.4} />
                                <p>Nenhum combo ou ciclo disponível no momento.</p>
                            </div>
                        ) : (
                            <div className="mktpl-fullwidth-list">
                                {tabProducts.combos.map(product => (
                                    <div
                                        key={product._id}
                                        className={`mktpl-combo-card ${product.alreadyPurchased ? 'mktpl-combo-card--owned' : ''}`}
                                    >
                                        <div className="mktpl-combo-top">
                                            <div className="mktpl-deck-badges">
                                                {product.alreadyPurchased && (
                                                    <span className="mktpl-owned-chip">
                                                        <Check size={11} strokeWidth={3} /> Adquirido
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mktpl-combo-body">
                                            <div className="mktpl-combo-info">
                                                <h3 className="mktpl-combo-title">{product.title}</h3>
                                                <p className="mktpl-combo-subtitle">Estrutura fechada para ciclos específicos do curso.</p>
                                                {product.description && (
                                                    <p className="mktpl-combo-desc">{product.description}</p>
                                                )}
                                            </div>

                                            <div className="mktpl-combo-footer">
                                                <div className="mktpl-combo-price-block">
                                                    <span className="mktpl-combo-price-value">{formatPrice(getMinPrice(product))}</span>
                                                </div>
                                                <button
                                                    className="mktpl-combo-cta"
                                                    onClick={() => navigate(`/marketplace/${product._id}`)}
                                                >
                                                    Ver detalhes
                                                    <ArrowRight size={15} strokeWidth={2} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* ── DECKS INDIVIDUAIS ── */}
                {activeTab === 'decks' && (
                    <div className="mktpl-section">

                        {/* Bloco explicativo */}
                        <div className="mktpl-decks-info-block">
                            <div className="mktpl-decks-info-right">
                                <strong>Tudo que você precisa para dominar um conteúdo específico.</strong>
                                <ul>
                                    <li>Flashcards inteligentes com repetição espaçada</li>
                                    <li>Apostilas interativas integradas ao conteúdo</li>
                                </ul>
                                <span>
                                    Para acesso completo — assistente e questões ilimitadas — conheça o{' '}
                                    <button className="mktpl-decks-info-link" onClick={() => setActiveTab('access_all')}>
                                        Plano Integral
                                    </button>.
                                </span>
                            </div>
                        </div>

                        {/* Busca */}
                        <div className="mktpl-search-row">
                            <div className="mktpl-search-box">
                                <Search size={17} />
                                <input
                                    type="text"
                                    placeholder="Buscar decks por nome ou descrição..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                />
                                {searchQuery && (
                                    <button className="mktpl-search-clear" onClick={() => setSearchQuery('')}>
                                        <X size={15} />
                                    </button>
                                )}
                            </div>
                            <span className="mktpl-results-count">{filteredDecks.length} resultado(s)</span>
                        </div>

                        {filteredDecks.length === 0 ? (
                            <div className="mktpl-empty">
                                <BookOpen size={52} strokeWidth={1.4} />
                                <p>{searchQuery ? 'Nenhum resultado para sua busca.' : 'Nenhum deck disponível no momento.'}</p>
                            </div>
                        ) : (
                            <div className="mktpl-decks-grid">
                                {filteredDecks.map(product => (
                                    <div
                                        key={product._id}
                                        className={`mktpl-deck-card ${product.alreadyPurchased ? 'mktpl-purchased' : ''}`}
                                    >
                                        <div className="mktpl-deck-badges">
                                            {product.alreadyPurchased && (
                                                <span className="mktpl-owned-chip">
                                                    <Check size={11} strokeWidth={3} /> Adquirido
                                                </span>
                                            )}

                                        </div>

                                        <div className="mktpl-deck-body">
                                            <div className="mktpl-deck-title-row">
                                                <h3 className="mktpl-deck-title">{product.title}</h3>
                                                {hasItem(product._id) && !product.alreadyPurchased && (
                                                    <span className="mktpl-cart-chip">
                                                        <Check size={11} strokeWidth={3} /> Carrinho
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mktpl-deck-subtitle">{getProductSubtitle(product)}</p>
                                            {product.description && (
                                                <p className="mktpl-deck-desc">{product.description}</p>
                                            )}
                                        </div>

                                        <div className="mktpl-deck-footer">
                                            <div className="mktpl-deck-price">
                                                <span className="mktpl-price-value">{formatPrice(getMinPrice(product))}</span>
                                            </div>
                                            <button
                                                className="mktpl-btn-detail"
                                                onClick={() => navigate(`/marketplace/${product._id}`)}
                                            >
                                                Ver detalhes
                                                <ArrowRight size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
};

export default MarketplacePage;